import apiClient from "./client";

// fetch list of ZoneDto
export function fetchZones() {
  return apiClient.get("/zones").then((res) => res.data);
}

// fetch one zone by ID
export function fetchZone(id) {
  return apiClient.get(`/zones/${encodeURIComponent(id)}`).then((res) => res.data);
}

// fetch list of zone types (for selects)
export function fetchZoneTypes() {
  return apiClient.get("/zones/types").then((res) => res.data);
}

// fetch raw geoJson payload for one zone and parse it
export async function fetchZoneGeoJson(zoneId) {
  try {
    const response = await apiClient.get(`/zones/geoJson/${encodeURIComponent(zoneId)}`);
    const data = response?.data;

    const parseRawGeojson = (raw) => {
      if (!raw) return null;
      if (typeof raw === "string") {
        return JSON.parse(raw);
      }
      return raw;
    };

    const normalizeFeatureCollection = (geojson, overlay = {}) => {
      if (!geojson) return null;

      let normalized = geojson;
      if (normalized.type === "Feature") {
        normalized = { type: "FeatureCollection", features: [normalized] };
      }
      if (normalized.type !== "FeatureCollection" || !Array.isArray(normalized.features)) {
        return null;
      }

      const colour = overlay?.Colour || overlay?.colour || "#1E88E5";
      const overlayName = overlay?.OverlayName || overlay?.overlayName || "Overlay";
      const source = overlay?.Source || overlay?.source || "";

      return {
        type: "FeatureCollection",
        features: normalized.features.map((feature) => ({
          ...feature,
          properties: {
            ...(feature?.properties || {}),
            colour: feature?.properties?.colour || colour,
            overlayName: feature?.properties?.overlayName || overlayName,
            source: feature?.properties?.source || source,
          },
        })),
      };
    };
    
    // API returns List<OverlayDto> - array of objects with Data property (capital D from C#)
    if (Array.isArray(data) && data.length > 0) {
      const merged = [];
      for (const overlay of data) {
        const raw = overlay?.Data ?? overlay?.data ?? null;
        if (!raw) {
          continue;
        }
        const parsed = parseRawGeojson(raw);
        const normalized = normalizeFeatureCollection(parsed, overlay);
        if (normalized?.features?.length) {
          merged.push(...normalized.features);
        }
      }
      return merged.length ? { type: "FeatureCollection", features: merged } : null;
    }
    
    // If not an array, maybe it's a single object
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const raw = data.Data ?? data.data ?? data;
      const parsed = parseRawGeojson(raw);
      return normalizeFeatureCollection(parsed, data);
    }
    
    return null;
  } catch (error) {
    return null;
  }
}

export function createZone(payload) {
  // payload: { name, typeId, description, geometry, … }
  return apiClient.post("/zones", payload).then((res) => res.data);
}

// update an existing zone
export function updateZone(id, payload) {
  return apiClient.put(`/zones/${encodeURIComponent(id)}`, payload).then((res) => res.data);
}

// (Optional) delete a zone
export function deleteZone(id) {
  return apiClient.delete(`/zones/${encodeURIComponent(id)}`).then((res) => res.data);
}

// COMMENTED OUT: MBTiles generation - replaced with PMTiles
/*
// Generate offline map (MBTiles) for a zone
export function generateOfflineMap(zoneId) {
  return apiClient.post(`/zones/${encodeURIComponent(zoneId)}/generate-offline-map`).then((res) => res.data);
}
*/

// NEW: Generate offline map (PMTiles) for a zone
export function generateOfflineMap(zoneId) {
  return apiClient.post(`/zones/${encodeURIComponent(zoneId)}/generate-offline-map`).then((res) => res.data);
}

// Check if offline map exists for a zone
export function checkOfflineMapStatus(zoneId) {
  return apiClient.get(`/zones/${encodeURIComponent(zoneId)}/offline-map/status`).then((res) => res.data);
}

export function downloadOfflineMap(zoneId) {
  return apiClient.get(`/zones/${encodeURIComponent(zoneId)}/offline-map`, {
    responseType: "blob",
  }).then((res) => res.data);
}

export function deleteOfflineMap(zoneId) {
  return apiClient.delete(`/zones/${encodeURIComponent(zoneId)}/offline-map`).then((res) => res.data);
}
